//"use client"

import { useEffect, useRef, useState } from "react"
import { Helmet } from "react-helmet"
import { serviceSchemaJson } from "../utils/serviceSchema";
import { breadcrumbSchemaJson } from "../utils/breadcrumbSchema";
import "./GraphicsDesigningSolution.css"

function GraphicsDesigningSolution({ scrollToAppointment }) {
  const [isVisible, setIsVisible] = useState(false)
  const [counters, setCounters] = useState({ engagement: 50, visualAppeal: 1.5, satisfaction: 96 })
  const statsRef = useRef(null)

  // Intersection Observer for triggering animations
  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true)
          startCounterAnimation()
        }
      },
      { threshold: 0.3 },
    )

    if (statsRef.current) {
      observer.observe(statsRef.current)
    }

    return () => observer.disconnect()
  }, [])

  // Counter animation function
  const startCounterAnimation = () => {
    const targets = { engagement: 50, visualAppeal: 1.5, satisfaction: 96 }
    const duration = 2000 // 2 seconds
    const steps = 60
    const stepDuration = duration / steps
    let currentStep = 0

    const timer = setInterval(() => {
      currentStep++
      const progress = currentStep / steps
      const easeOutQuart = 1 - Math.pow(1 - progress, 4)

      setCounters({
        engagement: Math.round(targets.engagement * easeOutQuart),
        visualAppeal: Math.round(targets.visualAppeal * easeOutQuart * 10) / 10,
        satisfaction: Math.round(targets.satisfaction * easeOutQuart),
      })

      if (currentStep >= steps) {
        clearInterval(timer)
        setCounters(targets)
      }
    }, stepDuration)
  }

  return (
    <div className="main_div_work_speaks">
      <Helmet>
<meta property="og:title" content="Graphic Design Services | 360 SynergyTech" />
<meta property="og:url" content="https://360synergytech.com/graphics-designing" />
<meta property="og:image" content="https://360synergytech.com/synergy-logo.png" />
<meta property="og:description" content="Elevate your brand with 360 SynergyTech's Graphics Designing services. We craft stunning visuals to captivate audiences and strengthen brand identity." />

<meta name="twitter:card" content="summary_large_image" />
<meta name="twitter:title" content="Graphic Design Services | 360 SynergyTech" />
<meta name="twitter:description" content="Elevate your brand with 360 SynergyTech's Graphics Designing services. We craft stunning visuals to captivate audiences and strengthen brand identity." />
<meta name="twitter:image" content="https://360synergytech.com/synergy-logo.png" />

        <title>Graphic Design Services | 360 SynergyTech</title>
        <meta name="description" content="Elevate your brand with 360 SynergyTech's Graphics Designing services. We craft stunning visuals to captivate audiences and strengthen brand identity." />
        <link rel="canonical" href="https://360synergytech.com/graphics-designing" />
        <script type="application/ld+json">{serviceSchemaJson({ name: "Graphic Design Services", description: "Elevate your brand with 360 SynergyTech's Graphics Designing services. We craft stunning visuals to captivate audiences and strengthen brand identity.", path: "/graphics-designing" })}</script>
        <script type="application/ld+json">{breadcrumbSchemaJson([{ name: "Services", path: "/services" }, { name: "Graphics Designing", path: "/graphics-designing" }])}</script>
      </Helmet>
      
      <div className="row m-0 p-0">
        <div className="col-lg-2 col-md-1 col-sm-12 col-12"></div>
        <div className="col-lg-8 col-md-10 col-sm-12 col-12">
          <div className="row m-0 p-0">
            <div className="col-lg-10 col-12">
              <h2 className="heading_work_speaks">Graphics Designing solutions to enhance your brand</h2>
              <p className="heading_work_speaks_para">
                At 360 SynergyTech, our Graphics Designing services create visually compelling designs to elevate your brand identity and engage your audience. From logos to UI/UX designs, we deliver creative solutions that align with your business goals. Our expertise ensures stunning visuals that leave a lasting impression across digital and print media.
              </p>
            </div>
            <div className="col-lg-2 col-12">
              <div className="button-container">
                <button
                  className="btn_lets_talk"
                  onClick={scrollToAppointment}
                  aria-label="Schedule a consultation with our experts"
                >
                 Get Help
                </button>
              </div>
            </div>
          </div>

          <div style={{ marginTop: "100px" }}></div>

          <div ref={statsRef} className="stats_section">
            <p className="stats_title">Captivating audiences with stunning graphic designs</p>
            <div className="stats_cards_container">
              <div className="stat_card stat_card_blue">
                <p className="stat_number">{`${counters.engagement}%`}</p>
                <p className="stat_description">Increase in brand engagement</p>
              </div>
              <div className="stat_card stat_card_orange">
                <p className="stat_number">{`${counters.visualAppeal}X`}</p>
                <p className="stat_description">Growth in visual appeal metrics</p>
              </div>
              <div className="stat_card stat_card_blue">
                <p className="stat_number">{`${counters.satisfaction}%`}</p>
                <p className="stat_description">Design satisfaction rate</p>
              </div>
            </div>
          </div>

          <div style={{ marginTop: "80px" }}></div>

          {/* Content Cards Section */}
          <div className="content_cards_section">
            <div className="content_card">
              <div className="content_card_header">
                <h2 className="content_card_title">Logo and Branding Design</h2>
              </div>
              <div className="content_card_body">
                <p className="content_card_text">
                  Craft unique logos and branding materials that define your identity. We create designs that reflect your values and resonate with your target audience.
                </p>
              </div>
            </div>

            <div className="content_card">
              <div className="content_card_header">
                <h2 className="content_card_title">UI/UX Design Services</h2>
              </div>
              <div className="content_card_body">
                <p className="content_card_text">
                  Enhance user interfaces with our UI/UX design expertise. We focus on intuitive layouts and interactive elements to improve user satisfaction and engagement.
                </p>
              </div>
            </div>

            <div className="content_card">
              <div className="content_card_header">
                <h2 className="content_card_title">Print Media Design</h2>
              </div>
              <div className="content_card_body">
                <p className="content_card_text">
                  Develop professional print materials like brochures, posters, and business cards. Our designs ensure consistency across all offline marketing channels.
                </p>
              </div>
            </div>

            <div className="content_card">
              <div className="content_card_header">
                <h2 className="content_card_title">Motion Graphics and Animation</h2>
              </div>
              <div className="content_card_body">
                <p className="content_card_text">
                  Add dynamic visuals with motion graphics and animations. We create engaging videos and effects to boost your digital marketing campaigns.
                </p>
              </div>
            </div>

            <div className="content_card">
              <div className="content_card_header">
                <h2 className="content_card_title">Brand Guideline Development</h2>
              </div>
              <div className="content_card_body">
                <p className="content_card_text">
                  Establish a consistent brand presence with our guideline development. We provide detailed manuals for colors, typography, and usage to maintain brand integrity.
                </p>
              </div>
            </div>
          </div>
        </div>
        <div className="col-lg-2 col-md-1 col-sm-12 col-12"></div>
      </div>
    </div>
  )
}

export default GraphicsDesigningSolution
